import {
  AuditOutlined,
  CheckSquareOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import { CourseRegistrationDTO } from "../../interfaces/course-registration.interface";
import CBStepper from "../shared/CBStepper/CBStepper";
import CourseRegistrationStep1 from "./components/course-registration-step1";
import CourseRegistrationStep2 from "./components/course-registration-step2";
import CourseRegistrationStep3 from "./components/course-registration-step3";
import CourseRegistrationSummary from "./components/course-registration-summary";
import { updateUser as updateUserDto } from "../../services/users.service";
import { UserContext } from "../../context/user.context";
import { Spin } from "antd";
import { Navigate, useParams } from "react-router-dom";
import { registerCourse } from "../../services/course.service";
import User from "../../interfaces/user.interface";
import { MyCoursesSummaryInterface } from "../../interfaces/my-courses-summary.interface";
import {
  getCoursesSummaryByStudent,
  updateStudentCourse,
} from "../../services/students-courses.service";
export interface summ {
  payMethod: string;
  link: string;
}
const CourseRegistration = () => {
  const { id } = useParams();
  const { user, updateUser } = useContext(UserContext);
  const [summary, setSummary] = useState<summ>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepPosition, setStepPosition] = useState<number>(0);
  const [objCollector, setObjCollector] = useState<
    CourseRegistrationDTO | undefined
  >(undefined);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const changeStep = (step: number) => setStepPosition(stepPosition + step);
  const stepCollector = (
    formObj: CourseRegistrationDTO | undefined,
    step: number,
  ) => {
    if (stepPosition === 0) {
      setObjCollector(formObj);
      patchUser(formObj!);
    }

    if (stepPosition === 1 && objCollector?.waitList) {
      postRegistration("WaitList");
      return;
    }

    changeStep(step);
  };

  const patchUser = async (formObj: CourseRegistrationDTO) => {
    const props: User = {
      id: user!.id,
      association: user?.association ?? "",
      address: formObj.address ?? user?.address,
      aptSuite: formObj.aptSuite ?? user!.aptSuite,
      city: formObj.city ?? user?.city,
      contactEmail: formObj.contactEmail ?? user?.contactEmail,
      country: formObj.country ?? user?.country,
      createdAt: user!.createdAt,
      designation: formObj.designation,
      email: user!.email,
      firstName: formObj.firstName ?? user?.firstName,
      lastName: formObj.lastName ?? user?.lastName,
      degreeYear: formObj.degreeYear ?? user?.degreeYear,
      member: formObj.member ?? user?.member,
      membership: formObj.membership ?? user?.membership ?? "",
      employmentStatus: formObj.employmentStatus ?? user?.employmentStatus,
      employer: formObj.employer ?? user?.employer ?? "",
      mobile: user!.mobile,
      notes: user!.notes,
      phone: formObj.phone ?? user?.phone,
      zipCode: formObj.zipCode ?? user?.zipCode,
      province: formObj.province ?? user?.province,
      role: user!.role,
      status: "1",
      title: formObj.title ?? user?.title,
      profilePicture: user!.profilePicture,
      institutionId: user!.institutionId,
      educations: user!.educations,
    };

    await updateUserDto(props);
    updateUser(props);
  };

  const postRegistration = async (payMethod: string) => {
    setIsLoading(true);
    let payload = {
      courseId: id!,
      userId: user?.id!,
      courseCostId: objCollector?.course?.costId!,
      licensePurchase: objCollector?.licensePurchase,
      waitList: objCollector?.waitList!,
      paymentType: payMethod,
    };

    const res =
      objCollector?.paymentMethod === "WaitList"
        ? await updateStudentCourse(payload)
        : await registerCourse(payload);

    setSummary({
      payMethod,
      link: `${process.env.REACT_APP_API_URL}/students-courses/${res?.data?.studentCourse?.id ?? ""}/invoice/${res?.data?.invoice?.id ?? ""}/file`,
    });
    setIsLoading(false);
  };

  const checkStudentSummary = (summary: MyCoursesSummaryInterface[]) => {
    if (summary[0]) {
      const mainInvoice = summary[0]?.invoices?.reduce(
        (acc, invoice) => {
          acc.baseCost += invoice.baseCost;
          acc.dueDateDiscount += invoice.dueDateDiscount;
          acc.licenseCost += invoice.licenseCost;
          acc.memberDiscount += invoice.memberDiscount;
          acc.subtotal += invoice.subtotal;
          acc.taxes += invoice.taxes;
          return acc;
        },
        {
          baseCost: 0,
          dueDateDiscount: 0,
          licenseCost: 0,
          memberDiscount: 0,
          subtotal: 0,
          taxes: 0,
        },
      );

      const paymentAmounts = summary[0]?.payments?.reduce(
        (acc, payment) => {
          if (payment.status === "Completed") {
            acc.completedAmount += payment.amount;
            acc.completedType = payment.type;
          } else {
            acc.pendingAmount += payment.amount;
          }
          return acc;
        },
        {
          completedAmount: 0,
          completedType: "Cheque",
          pendingAmount: 0,
        },
      );

      if (summary[0]?.status === "Registered") {
        setIsRegistered(true);
      } else {
        setObjCollector({
          id: user?.id,
          email: user?.email ?? "",
          contactEmail: user?.contactEmail ?? "",
          phone: user?.phone ?? "",
          lastName: user?.lastName ?? "",
          firstName: user?.firstName ?? "",
          degreeYear: user?.degreeYear ?? "",
          member: user?.member ?? "",
          membership: user?.membership ?? "",
          employmentStatus: user?.employmentStatus ?? "",
          employer: user?.employer ?? "",
          country: user?.country ?? "",
          province: user?.province ?? "",
          city: user?.city ?? "",
          address: user?.address ?? "",
          aptSuite: user?.aptSuite ?? "",
          zipCode: user?.zipCode ?? "",
          designation: user?.designation ?? "",
          waitList: summary[0]?.status === "In Progress" ? true : false,
          title: user?.title ?? "",
          paymentMethod:
            summary[0]?.status === "In Progress"
              ? "WaitList"
              : paymentAmounts.completedType,
          amountPayed: `${(paymentAmounts.completedAmount / 100).toFixed(2)}`,
          balance: `${(paymentAmounts.pendingAmount / 100).toFixed(2)}`,
          totalFees: "0.00",
          course: {
            id: summary[0]?.course?.id,
            code: summary[0]?.course?.code,
            costId: summary[0]?.courseCostId,
            name: summary[0]?.course?.name,
            registrationType: summary[0]?.courseCost?.registrationType,
            paymentMethods: summary[0]?.courseCost?.paymentMethods,
            price: `${(mainInvoice.baseCost / 100).toFixed(2)}`,
            license: [
              {
                enable: 1,
                title: "License",
                leyend: "License",
                value: `${(mainInvoice.licenseCost / 100).toFixed(2)}`,
                id: 0,
                courseId: 0,
              },
            ],
            discountBeforeDueDate: `${(mainInvoice.dueDateDiscount / 100).toFixed(2)}`,
            memberDiscount: `${(mainInvoice.memberDiscount / 100).toFixed(2)}`,
            subtotalPrice: `${(mainInvoice.subtotal / 100).toFixed(2)}`,
            gst: [
              {
                enable: 1,
                title: "GST",
                leyend: `${((mainInvoice.taxes * 100) / mainInvoice.subtotal).toFixed(2)}`,
                value: `${((mainInvoice.taxes * 100) / mainInvoice.subtotal).toFixed(2)}`,
                id: 0,
                courseId: 0,
              },
            ],
          },
        });
        setStepPosition(2);
      }
    }
  };

  const getStudentSummary = async () => {
    checkStudentSummary(await getCoursesSummaryByStudent(id ?? "0"));
  };

  useEffect(() => {
    getStudentSummary();
  }, [id]);

  const steps = [
    {
      title: "Registration",
      icon: <UserAddOutlined />,
      content: (
        <CourseRegistrationStep1
          changeStep={(o) => stepCollector(o, +1)}
          registrationSelected={
            objCollector?.course?.key ? [objCollector?.course?.key] : []
          }
        />
      ),
    },
    {
      title: "Details",
      icon: <AuditOutlined />,
      content: (
        <CourseRegistrationStep2
          changeStep={(s) => stepCollector(undefined, s)}
          data={objCollector?.course!}
          waitList={objCollector?.waitList!}
        />
      ),
    },
    {
      title: "Invoice",
      icon: <CheckSquareOutlined />,
      content: (
        <CourseRegistrationStep3
          changeStep={(s) => stepCollector(undefined, s)}
          data={objCollector!}
          postRegistration={(pay: string) => postRegistration(pay)}
          setSummary={(payMethod: string, link: string) =>
            setSummary({ payMethod, link })
          }
        />
      ),
    },
  ];

  return (
    <div className="flex justify-center pr-4 pt-5">
      <div className="flex flex-col gap-6 p-4 max-w-5xl w-full">
        {isLoading && <Spin size="large" fullscreen />}
        {isRegistered && (
          <Navigate to={`/SEABC/courses/${id}`} replace={true} />
        )}
        {!summary ? (
          <CBStepper steps={steps} stepPosition={stepPosition} />
        ) : (
          <CourseRegistrationSummary data={objCollector!} summary={summary} />
        )}
      </div>
    </div>
  );
};

export default CourseRegistration;
